import {
  APIModelAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
  ArrowType,
  FieldGlobalAttributes,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  getEmptyComponentGraphData,
  GlobalReferenceTypeId,
  GraphModelShape,
  ReferenceLabelSource,
  SimpleReferenceModel,
} from '@ardoq/data-model';
import {
  isFormattedByField,
  isFormattedByReferenceType,
  referenceLabelOperations,
} from '@ardoq/reference-operations';
import { fieldInterface } from '@ardoq/field-interface';
import { fieldUtils, referenceUtils } from '@ardoq/scope-data';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { usersOperations } from '@ardoq/core';
import { referenceInterface } from '@ardoq/reference-interface';
import { filterInterface } from '@ardoq/filter-interface';
import { modelOperations } from 'models/modelOperations';
import { localeCompare, type Locale } from '@ardoq/locale';

const referenceDescriptorsToGraphData = (
  referenceDescriptors: {
    reference: APIReferenceAttributes;
    referenceType: APIReferenceType | undefined;
  }[],
  locale: Locale
): GraphModelShape => {
  const sourceMap = new Map<ArdoqId, LinkedComponent[]>();
  const targetMap = new Map<ArdoqId, LinkedComponent[]>();
  const referenceTypeMap = new Map<string, GlobalReferenceTypeId[]>();
  const referenceMap = new Map<ArdoqId, SimpleReferenceModel>();

  referenceDescriptors.forEach(({ reference, referenceType }) => {
    referenceMap.set(reference._id, {
      sourceId: reference.source,
      targetId: reference.target,
    });

    if (referenceType) {
      const globalTypeId = referenceUtils.toGlobalReferenceTypeId(reference);
      let typeIds = referenceTypeMap.get(referenceType.name);
      if (!typeIds) {
        typeIds = [];
        referenceTypeMap.set(referenceType.name, typeIds);
      }
      if (!typeIds.includes(globalTypeId)) {
        typeIds.push(globalTypeId);
      }
    }

    let targetIds = sourceMap.get(reference.source);
    if (!targetIds) {
      targetIds = [];
      sourceMap.set(reference.source, targetIds);
    }
    targetIds.push({
      componentId: reference.target,
      referenceId: reference._id,
    });

    let sourceIds = targetMap.get(reference.target);
    if (!sourceIds) {
      sourceIds = [];
      targetMap.set(reference.target, sourceIds);
    }
    sourceIds.push({
      componentId: reference.source,
      referenceId: reference._id,
    });
  });

  const referenceTypes = Array.from(referenceTypeMap)
    .map(([name, typeIds]) => ({ name, typeIds }))
    .sort((a, b) => localeCompare(a.name, b.name, locale));

  sourceMap.forEach(list => Object.freeze(list));
  targetMap.forEach(list => Object.freeze(list));
  referenceMap.forEach(list => Object.freeze(list));

  return {
    ...getEmptyComponentGraphData(),
    sourceMap,
    targetMap,
    referenceMap,
    referenceTypes,
  };
};

export const referenceListToGraphData = (
  references: APIReferenceAttributes[],
  models: APIModelAttributes[],
  locale: Locale
) => {
  const refTypeLookup = modelOperations.makeReferenceTypeLookup(models);
  const referenceDescriptors = references.map(
    (reference: APIReferenceAttributes) => {
      const referenceType = modelOperations.getReferenceType(
        refTypeLookup,
        reference.model,
        reference.type
      );
      return { reference, referenceType };
    }
  );
  return referenceDescriptorsToGraphData(referenceDescriptors, locale);
};

export const getCurrentComponentLabelFormattingSetting = () =>
  filterInterface.getComponentLabelFilter().value;

const getCurrentReferenceLabelFilteringSetting = () =>
  filterInterface.getReferenceLabelFilter().value;

export const getCardinalityArrowTypes = () => [
  ArrowType.ONE,
  ArrowType.ONE_MANDATORY,
  ArrowType.MANY,
  ArrowType.ONE_OR_MANY,
  ArrowType.ZERO_OR_ONE,
  ArrowType.ZERO_OR_MANY,
];

export const getReferenceLabelFormattingFieldLabel = (): string | null => {
  const currentSetting = getCurrentReferenceLabelFilteringSetting();
  switch (currentSetting) {
    case ReferenceLabelSource.REFERENCE_TYPE:
    case ReferenceLabelSource.DISPLAY_TEXT:
    case ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE:
      return null;
    default: {
      const field = fieldInterface.getGlobalFieldByName(currentSetting);

      return field ? (field.label ?? field.name) : null;
    }
  }
};

export const getReferenceLabelFormattingFieldValue = (
  referenceId: ArdoqId
): string | null => {
  if (!referenceInterface.isReference(referenceId)) {
    return null;
  }

  const activeRefLabelFilter = filterInterface.getReferenceLabelFilter();

  const currentSetting = getCurrentReferenceLabelFilteringSetting();
  const fieldValue = currentSetting
    ? referenceInterface.get(referenceId)?.[currentSetting]
    : null;

  const fieldAttributes = referenceInterface
    .getFields(referenceId)
    .find(field => field.name === currentSetting);
  const excludeTime = !activeRefLabelFilter.includeTime;

  if (!fieldAttributes) {
    return null;
  }

  // i18nLocale$.state / orgUsers$.state OK here: imperative reference label helper invoked
  // from one-shot callers (filter dropdowns, AG Grid value formatters).
  return (
    fieldUtils.fieldAttributesToLabel({
      fieldAttributes,
      value: fieldValue,
      excludeTime,
      textAreaFormat: 'text',
      formatUserFieldFn: val =>
        usersOperations.formatUserField(orgUsers$.state.byId, val),
      i18nLocale: i18nLocale$.state,
    }) ??
    fieldValue ??
    null
  );
};

const getReferenceLabelByField = (
  reference: APIReferenceAttributes,
  field: FieldGlobalAttributes | null,
  excludeTime: boolean
) => {
  if (!field) {
    return '';
  }

  const { fieldLabel, fieldValue } =
    referenceLabelOperations.getFormattingByFieldInfo(reference, field);

  // i18nLocale$.state / orgUsers$.state OK here: imperative reference label helper invoked
  // from one-shot callers without reactive context.
  const formattedFieldValue = fieldUtils.fieldAttributesToLabel({
    fieldAttributes: field,
    value: fieldValue,
    excludeTime,
    textAreaFormat: 'text',
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsers$.state.byId, val),
    i18nLocale: i18nLocale$.state,
  });

  return referenceLabelOperations.concatenateFieldValueLabelParts(
    fieldLabel,
    formattedFieldValue
  );
};

export const getDisplayLabel = (
  reference: APIReferenceAttributes,
  field: FieldGlobalAttributes | null,
  referenceType: APIReferenceType,
  filterValue: string,
  excludeTime: boolean
): string | null => {
  if (isFormattedByField(filterValue)) {
    return getReferenceLabelByField(reference, field, excludeTime);
  }

  if (!isFormattedByReferenceType(filterValue, reference)) {
    return reference.displayText ?? null;
  }

  return referenceType.name || null;
};
